import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Alert,
  Skeleton,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  ArrowBack,
  Receipt,
  Edit,
  PictureAsPdf,
  Download,
  Person,
  CalendarToday,
  AttachMoney,
  CheckCircle,
  Warning,
  Schedule
} from '@mui/icons-material'

import { useInvoice } from '../../hooks/useInvoices'
import { formatCurrency, formatDate, formatInvoiceStatus, formatDaysUntil } from '../../utils/formatters'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../../components/Auth/RoleGuard'
import InvoiceForm from '../../components/Invoices/InvoiceForm'
import PdfViewer from '../../components/Common/PdfViewer'

const InvoiceDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAdmin } = useAuthStore()
  const [showEditForm, setShowEditForm] = useState(false)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)

  const { data: invoice, isLoading, error } = useInvoice(id)
  
  // Debug log to see what invoice data we're getting
  React.useEffect(() => {
    console.log('🔍 DEBUG - InvoiceDetailPage - invoice data:', invoice)
    console.log('🔍 DEBUG - InvoiceDetailPage - invoice.id:', invoice?.id)
    console.log('🔍 DEBUG - InvoiceDetailPage - isLoading:', isLoading)
    console.log('🔍 DEBUG - InvoiceDetailPage - error:', error)
  }, [invoice, isLoading, error])

  const handleBack = () => {
    navigate('/invoices')
  }

  const handleEdit = () => {
    console.log('🔍 DEBUG - InvoiceDetailPage - handleEdit called')
    setShowEditForm(true)
  }

  const handleCloseEditForm = () => {
    console.log('🔍 DEBUG - InvoiceDetailPage - handleCloseEditForm called')
    setShowEditForm(false)
  }

  const handleViewClient = () => {
    if (invoice?.client?.id) {
      navigate(`/clients/${invoice.client.id}`)
    }
  }

  const handleViewPdf = async () => {
    if (invoice?.pdfSignedUrl) {
      setPdfViewerOpen(true)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAGADA': return 'success'
      case 'POR_COBRAR': return 'warning'
      case 'VENCIDA': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAGADA': return <CheckCircle />
      case 'POR_COBRAR': return <Schedule />
      case 'VENCIDA': return <Warning />
      default: return <Receipt />
    }
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Volver a Facturas
        </Button>
        <Alert severity="error">
          Error cargando factura: {error.message}
        </Alert>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Volver a Facturas
        </Button>
        <Skeleton variant="rectangular" width="100%" height={600} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant="outlined"
        >
          Volver
        </Button>
        
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Factura {invoice?.invoiceNumber}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={formatInvoiceStatus(invoice?.visualStatus || invoice?.status)}
              color={getStatusColor(invoice?.visualStatus || invoice?.status)}
              icon={getStatusIcon(invoice?.status)}
              variant="outlined"
            />
            {invoice?.status !== 'PAGADA' && (
              <Typography variant="body2" color="text.secondary">
                {formatDaysUntil(invoice?.dueDate)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {invoice?.pdfSignedUrl && (
            <Tooltip title="Ver PDF">
              <IconButton onClick={handleViewPdf} color="primary">
                <PictureAsPdf />
              </IconButton>
            </Tooltip>
          )}
          
          <RoleGuard requireAdmin>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={handleEdit}
            >
              Editar
            </Button>
          </RoleGuard>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Invoice Details */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Información de la Factura
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Número de Factura
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {invoice?.invoiceNumber}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Estado
                    </Typography>
                    <Chip
                      label={formatInvoiceStatus(invoice?.visualStatus || invoice?.status)}
                      color={getStatusColor(invoice?.visualStatus || invoice?.status)}
                      icon={getStatusIcon(invoice?.status)}
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Emisión
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(invoice?.issueDate)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Vencimiento
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(invoice?.dueDate)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Descripción
                    </Typography>
                    <Typography variant="body1">
                      {invoice?.description}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Client Info */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Información del Cliente
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  p: 2, 
                  backgroundColor: 'background.default',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                onClick={handleViewClient}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {invoice?.client?.name?.charAt(0)?.toUpperCase()}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {invoice?.client?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    RUT: {invoice?.client?.rut}
                  </Typography>
                  {invoice?.client?.email && (
                    <Typography variant="body2" color="text.secondary">
                      {invoice?.client?.email}
                    </Typography>
                  )}
                </Box>
                <Button variant="outlined" size="small">
                  Ver Cliente
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Amounts & PDF */}
        <Grid item xs={12} md={4}>
          {/* Amounts Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Desglose de Montos
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Monto Neto:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatCurrency(invoice?.netAmount)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    IVA ({invoice?.ivaPercentage}%):
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatCurrency(invoice?.ivaAmount)}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight={600}>
                    Total:
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {formatCurrency(invoice?.totalAmount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* PDF Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Archivo PDF
              </Typography>
              
              {invoice?.pdfSignedUrl ? (
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    p: 2, 
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    mb: 2
                  }}>
                    <PictureAsPdf color="error" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {invoice.invoiceNumber}.pdf
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Archivo disponible
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<PictureAsPdf />}
                      onClick={handleViewPdf}
                      fullWidth
                    >
                      Ver PDF
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleViewPdf}
                    >
                      Descargar
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <PictureAsPdf sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No hay archivo PDF asociado
                  </Typography>
                  <RoleGuard requireAdmin>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleEdit}
                      sx={{ mt: 1 }}
                    >
                      Subir PDF
                    </Button>
                  </RoleGuard>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Invoice Form */}
      <InvoiceForm
        open={showEditForm}
        onClose={handleCloseEditForm}
        invoice={invoice}
        title="Editar Factura"
      />

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={invoice?.pdfSignedUrl}
        title={`Factura ${invoice?.invoiceNumber}`}
        fileName={`${invoice?.invoiceNumber || 'factura'}.pdf`}
      />
    </Box>
  )
}

export default InvoiceDetailPage
